import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { 
  Camera, Upload, Image, Calendar, Trash2, Eye, EyeOff, 
  ChevronLeft, ChevronRight, TrendingUp, User, ArrowRight, RotateCcw
} from "lucide-react";
import { toast } from "sonner";
import ActionControls from "@/components/ActionControls";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "front", label: "Front", icon: "👤" },
  { id: "side", label: "Side", icon: "🧍" },
  { id: "back", label: "Back", icon: "🔙" }
];

const ProgressPhotos = () => {
  const [activeTab, setActiveTab] = useState("upload");
  const [photos, setPhotos] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [stats, setStats] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [comparisonCategory, setComparisonCategory] = useState("front");
  const [sliderPosition, setSliderPosition] = useState([50]);
  const [loading, setLoading] = useState(false);
  
  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadCategory, setUploadCategory] = useState("front");
  const [uploadWeek, setUploadWeek] = useState("1");
  const [uploadPhase, setUploadPhase] = useState("1");
  const [uploadNotes, setUploadNotes] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  
  // History for undo
  const [photoHistory, setPhotoHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  
  const fileInputRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchComparison(comparisonCategory);
  }, [comparisonCategory]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [photosRes, timelineRes, statsRes] = await Promise.all([
        axios.get(`${API}/progress-photos`, { headers }),
        axios.get(`${API}/progress-photos/timeline`, { headers }),
        axios.get(`${API}/progress-photos/stats`, { headers })
      ]);

      setPhotos(photosRes.data);
      setTimeline(timelineRes.data);
      setStats(statsRes.data);
      
      // Initialize history
      if (photoHistory.length === 0) {
        setPhotoHistory([photosRes.data]);
        setHistoryIndex(0);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleResetPhotos = async () => {
    if (!window.confirm('Delete ALL progress photos? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/progress-photos/all`, { headers });
      setPhotos([]);
      setTimeline([]);
      setStats(null);
      setComparison(null);
      setPhotoHistory([[]]);
      setHistoryIndex(0);
      toast.success('All photos deleted');
    } catch (error) {
      toast.error('Failed to delete photos');
    }
  };

  const fetchComparison = async (category) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/progress-photos/comparison?category=${category}`, { headers });
      setComparison(response.data);
    } catch (error) {
      console.error('Error fetching comparison:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
        toast.error('Please select a JPEG, PNG, or WebP image');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a photo first');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', uploadCategory);
      formData.append('week', uploadWeek);
      formData.append('phase', uploadPhase);
      formData.append('notes', uploadNotes);
      formData.append('is_public', isPublic);

      await axios.post(`${API}/progress-photos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Photo uploaded successfully!');
      setSelectedFile(null);
      setPreviewUrl(null);
      setUploadNotes("");
      fetchData();
    } catch (error) {
      toast.error('Failed to upload photo');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/progress-photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const getPhotoUrl = (photo) => {
    const token = localStorage.getItem('token');
    return `${API}/progress-photos/image/${photo.id}?token=${token}`;
  };

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tight uppercase">
              PROGRESS PHOTOS
            </h1>
            <p className="text-zinc-500 mt-1 text-sm">Track your transformation journey</p>
          </div>
          <ActionControls
            onReset={handleResetPhotos}
            showUndo={false}
            showRedo={false}
            variant="compact"
            resetLabel="Delete all photos"
          />
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="photo-stats">
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Camera className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-3xl font-heading font-black text-white">{stats.total_photos}</p>
                <p className="text-xs text-muted-foreground">Total Photos</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <Calendar className="w-8 h-8 text-secondary mx-auto mb-2" />
                <p className="text-3xl font-heading font-black text-white">{stats.weeks_documented}</p>
                <p className="text-xs text-muted-foreground">Weeks Documented</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-heading font-black text-white">
                  {stats.latest_photo_week ? stats.latest_photo_week - (stats.first_photo_week || 0) : 0}
                </p>
                <p className="text-xs text-muted-foreground">Weeks Progress</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4 text-center">
                <User className="w-8 h-8 text-accent mx-auto mb-2" />
                <p className="text-3xl font-heading font-black text-white">
                  {Object.values(stats.categories_coverage || {}).filter(v => v > 0).length}/3
                </p>
                <p className="text-xs text-muted-foreground">Poses Captured</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 w-full md:w-auto">
            <TabsTrigger value="upload" data-testid="tab-upload">Upload</TabsTrigger>
            <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
            <TabsTrigger value="compare" data-testid="tab-compare">Compare</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-6">
            <Card className="bg-card border-border" data-testid="upload-card">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Upload Progress Photo</CardTitle>
                <CardDescription>Document your transformation with weekly photos</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* File Upload Area */}
                <div 
                  className={`border-2 border-dashed rounded-sm p-8 text-center transition-colors cursor-pointer ${
                    previewUrl ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {previewUrl ? (
                    <div className="space-y-4">
                      <img 
                        src={previewUrl} 
                        alt="Preview" 
                        className="max-h-64 mx-auto rounded-sm"
                      />
                      <p className="text-sm text-muted-foreground">Click to change photo</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <Upload className="w-12 h-12 text-muted-foreground mx-auto" />
                      <div>
                        <p className="text-white font-medium">Click to upload photo</p>
                        <p className="text-sm text-muted-foreground">JPEG, PNG or WebP</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Category Selection */}
                <div className="space-y-2">
                  <Label>Photo Category</Label>
                  <div className="grid grid-cols-3 gap-3">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setUploadCategory(cat.id)}
                        className={`p-4 rounded-sm border-2 transition-all ${
                          uploadCategory === cat.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`category-${cat.id}`}
                      >
                        <span className="text-2xl block mb-1">{cat.icon}</span>
                        <p className="text-sm font-medium text-white">{cat.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Week and Phase */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Week</Label>
                    <Select value={uploadWeek} onValueChange={setUploadWeek}>
                      <SelectTrigger data-testid="week-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(20)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                            Week {i + 1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Phase</Label>
                    <Select value={uploadPhase} onValueChange={setUploadPhase}>
                      <SelectTrigger data-testid="phase-selector">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4, 5, 6, 7].map((phase) => (
                          <SelectItem key={phase} value={phase.toString()}>
                            Phase {phase}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Input
                    placeholder="How are you feeling this week?"
                    value={uploadNotes}
                    onChange={(e) => setUploadNotes(e.target.value)}
                    data-testid="notes-input"
                  />
                </div>

                {/* Public Toggle */}
                <div className="flex items-center justify-between p-4 bg-background rounded-sm border border-border">
                  <div className="flex items-center gap-3">
                    {isPublic ? <Eye className="w-5 h-5 text-primary" /> : <EyeOff className="w-5 h-5 text-muted-foreground" />}
                    <div>
                      <p className="text-sm font-medium text-white">Make photo public</p>
                      <p className="text-xs text-muted-foreground">Allow others to see this photo</p>
                    </div>
                  </div>
                  <Switch
                    checked={isPublic}
                    onCheckedChange={setIsPublic}
                    data-testid="public-toggle"
                  />
                </div>

                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || loading}
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold uppercase"
                  data-testid="upload-button"
                >
                  {loading ? 'Uploading...' : 'Upload Photo'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="space-y-6">
            <Card className="bg-card border-border" data-testid="timeline-card">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Photo Timeline</CardTitle>
                <CardDescription>Your transformation week by week</CardDescription>
              </CardHeader>
              <CardContent>
                {timeline.length > 0 ? (
                  <div className="space-y-6">
                    {timeline.map((week) => (
                      <div key={week.week} className="border-l-2 border-primary pl-6 pb-6 relative">
                        <div className="absolute -left-2 top-0 w-4 h-4 rounded-full bg-primary" />
                        <div className="flex items-center gap-3 mb-4">
                          <Badge className="bg-primary/10 text-primary border-primary/30">
                            Week {week.week}
                          </Badge>
                          <span className="text-sm text-muted-foreground">Phase {week.phase}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          {CATEGORIES.map((cat) => {
                            const photo = week.photos[cat.id];
                            return (
                              <div key={cat.id} className="space-y-2">
                                <p className="text-xs font-medium text-muted-foreground uppercase">{cat.label}</p>
                                {photo ? (
                                  <div className="relative aspect-[3/4] rounded-sm overflow-hidden bg-background">
                                    <img
                                      src={getPhotoUrl(photo)}
                                      alt={`${cat.label} - Week ${week.week}`}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="aspect-[3/4] rounded-sm border border-dashed border-border flex items-center justify-center">
                                    <span className="text-2xl opacity-30">{cat.icon}</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No photos uploaded yet</p>
                    <p className="text-sm mt-2">Start documenting your transformation!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compare Tab */}
          <TabsContent value="compare" className="space-y-6">
            <Card className="bg-card border-border" data-testid="compare-card">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Before & After</CardTitle>
                <CardDescription>See your transformation progress</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Category Selector */}
                <div className="flex justify-center gap-2">
                  {CATEGORIES.map((cat) => (
                    <Button
                      key={cat.id}
                      variant={comparisonCategory === cat.id ? "default" : "outline"}
                      onClick={() => setComparisonCategory(cat.id)}
                      className={comparisonCategory === cat.id ? "bg-primary" : ""}
                      data-testid={`compare-${cat.id}`}
                    >
                      {cat.icon} {cat.label}
                    </Button>
                  ))}
                </div>

                {comparison && comparison.before ? (
                  <div className="space-y-6">
                    {/* Comparison Slider */}
                    <div className="relative aspect-[3/4] max-w-md mx-auto rounded-sm overflow-hidden bg-background">
                      {/* Before Image (Full) */}
                      <img
                        src={getPhotoUrl(comparison.before)}
                        alt="Before"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      
                      {/* After Image (Clipped) */}
                      {comparison.after && (
                        <div 
                          className="absolute inset-0 overflow-hidden"
                          style={{ width: `${sliderPosition[0]}%` }}
                        >
                          <img
                            src={getPhotoUrl(comparison.after)}
                            alt="After"
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{ 
                              width: `${100 / (sliderPosition[0] / 100)}%`,
                              maxWidth: 'none'
                            }}
                          />
                        </div>
                      )}
                      
                      {/* Slider Line */}
                      {comparison.after && (
                        <div 
                          className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                          style={{ left: `${sliderPosition[0]}%`, transform: 'translateX(-50%)' }}
                        >
                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-lg">
                            <ChevronLeft className="w-4 h-4 text-black absolute -left-0.5" />
                            <ChevronRight className="w-4 h-4 text-black absolute -right-0.5" />
                          </div>
                        </div>
                      )}
                      
                      {/* Labels */}
                      <div className="absolute bottom-4 left-4 bg-black/70 px-2 py-1 rounded text-xs text-white">
                        Week {comparison.before.week}
                      </div>
                      {comparison.after && (
                        <div className="absolute bottom-4 right-4 bg-primary/90 px-2 py-1 rounded text-xs text-white">
                          Week {comparison.after.week}
                        </div>
                      )}
                    </div>

                    {/* Slider Control */}
                    {comparison.after && (
                      <div className="max-w-md mx-auto">
                        <Slider
                          value={sliderPosition}
                          onValueChange={setSliderPosition}
                          min={0}
                          max={100}
                          step={1}
                          className="w-full"
                          data-testid="comparison-slider"
                        />
                      </div>
                    )}

                    {/* Progress Info */}
                    <div className="flex justify-center items-center gap-4 text-center">
                      <div>
                        <p className="text-sm text-muted-foreground">Started</p>
                        <p className="text-lg font-bold text-white">Week {comparison.before.week}</p>
                      </div>
                      <ArrowRight className="w-6 h-6 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Current</p>
                        <p className="text-lg font-bold text-white">
                          Week {comparison.after?.week || comparison.before.week}
                        </p>
                      </div>
                      <div className="pl-4 border-l border-border">
                        <p className="text-sm text-muted-foreground">Progress</p>
                        <p className="text-lg font-bold text-primary">{comparison.weeks_between} weeks</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Image className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No {comparisonCategory} photos to compare</p>
                    <p className="text-sm mt-2">Upload at least one photo to see comparisons</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery" className="space-y-6">
            <Card className="bg-card border-border" data-testid="gallery-card">
              <CardHeader>
                <CardTitle className="text-xl font-heading font-bold text-white">Photo Gallery</CardTitle>
                <CardDescription>All your progress photos</CardDescription>
              </CardHeader>
              <CardContent>
                {photos.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {photos.map((photo) => (
                      <div 
                        key={photo.id} 
                        className="group relative aspect-[3/4] rounded-sm overflow-hidden bg-background"
                        data-testid={`gallery-photo-${photo.id}`}
                      >
                        <img
                          src={getPhotoUrl(photo)}
                          alt={`${photo.category} - Week ${photo.week}`}
                          className="w-full h-full object-cover"
                        />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <div className="flex items-center justify-between mb-2">
                              <Badge className="bg-primary/80 text-white text-xs">
                                Week {photo.week}
                              </Badge>
                              <span className="text-xs text-white capitalize">{photo.category}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="destructive"
                              className="w-full"
                              onClick={() => handleDelete(photo.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                        {/* Public indicator */}
                        {photo.is_public && (
                          <div className="absolute top-2 right-2">
                            <Eye className="w-4 h-4 text-white drop-shadow-lg" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Camera className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No photos in your gallery</p>
                    <p className="text-sm mt-2">Upload your first progress photo!</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProgressPhotos;
